import $ from '@nuskin/nuskinjquery';
import {ProductSearchFieldsEnum} from "./productSearchFieldsEnum.js";
import {ProductResultsEnum} from "./productResultsEnum.js";
import OrderType from "./orderType.js";
import {SolrProductAdaptor} from "./solrProductAdaptor.js";
import {SolrQueryUrlService} from "./solrQueryUrlService.js";
import {ConfigService} from '@nuskin/ns-util';

/**
 *
 * SolrQueryService
 *
 * @namespace SolrQueryService
 */
let SolrQueryService = function() {
    //---------------------------------------------------------------------------
    //
    //                           Public Methods
    //
    //---------------------------------------------------------------------------
    let publicMethods = {
        /**
         * Find all products including base and variant products that have a field that matches the searchTerm.
         * If the search term doesn't contain any wild characters we will be search for an exact match for the
         * value of searchTerm.
         *
         * @memberof SolrQueryService
         * @example
         * SolrQueryService.getAllProductsBySearchTerm(searchTerm, language, market, rows, start)
         * @param searchTerm - Examples: Age, Age*, *Age*, etc.
         * @param language - Example: "en"
         * @param market   - Example: "US"
         * @param rows - Number of results to return
         * @param start - Used for pagination.
         *
         * @returns A promise to the result which may contain general products, base products, and variant products.
         */
        // getAllProductsBySearchTerm: function(searchTerm, language, market, rows, start) {
        //     return getProductsBySearchTerm(ProductResultsEnum.ALL_PRODUCTS, searchTerm, language, market, rows, start);
        // },

        getAllProductsBySearchTerm: function(searchTerm, language, market, rows, start, productSearchFields, orderTypes) {
            return getProductsBySearchTerm(
                ProductResultsEnum.ALL_PRODUCTS, searchTerm, language, market, rows, start, productSearchFields, orderTypes
            );
        },

        /**
         * Find all products including base and variant products that have a field that matches the searchTerm.
         * If the search term doesn't contain any wild characters we will be search for an exact match for the
         * value of searchTerm.
         *
         * @memberof SolrQueryService
         * @example
         * SolrQueryService.getBasicProductsBySearchTerm(searchTerm, language, market, rows, start)
         * @param searchTerm - Examples: Age, Age*, *Age*, etc.
         * @param language - Example: "en"
         * @param market   - Example: "US"
         * @param rows - Number of results to return
         * @param start - Used for pagination.
         *
         * @returns A promise to the result which may contain general products, base products, and variant products.
         */
        // getBasicProductsBySearchTerm: function(searchTerm, language, market, rows, start) {
        //     return getBasicProductsBySearchTerm(ProductResultsEnum.ALL_PRODUCTS, searchTerm, language, market, rows, start);
        // },

        getBasicProductsBySearchTerm: function(searchTerm, language, market, rows, start, productSearchFields, productResultTypes, orderTypes) {
            return getBasicProductsBySearchTerm(productResultTypes, searchTerm, language, market, rows, start, productSearchFields, orderTypes);
        },

        /**
         * Find products and base products that have a field that matches the searchTerm. If the search term doesn't
         * contain any wild characters we will be search for an exact match for the value of searchTerm.
         *
         * @memberof SolrQueryService
         * @example
         * SolrQueryService.getProductsAndBasesBySearchTerm(searchTerm, language, market, rows, start)
         * @param searchTerm - Examples: Age, Age*, *Age*, etc.
         * @param language - Example: "en"
         * @param market   - Example: "US"
         * @param rows - Number of results to return
         * @param start - Used for pagination.
         * @param orderTypes - Example: [OrderType.ADR, OrderType.ORDER, OrderType.ZPO] -- default: [OrderType.ADR, OrderType.ORDER]
         *
         * @returns A promise to the result which may contain general products and base products. Variant products are excluded.
         */
        // getProductsAndBasesBySearchTerm: function(searchTerm, language, market, rows, start) {
        //     return getProductsBySearchTerm(ProductResultsEnum.PRODUCTS_AND_BASE_PRODUCTS, searchTerm, language, market, rows, start);
        // },
        getProductsAndBasesBySearchTerm: function(searchTerm, language, market, rows, start, productSearchFields, orderTypes) {
            return getProductsBySearchTerm(
                ProductResultsEnum.PRODUCTS_AND_BASE_PRODUCTS, searchTerm, language, market, rows, start, productSearchFields, orderTypes
            );
        },

        /**
         * Find products and variant products that have a field that matches the searchTerm. If the search term doesn't
         * contain any wild characters we will be search for an exact match for the value of searchTerm.
         *
         * @memberof SolrQueryService
         * @example
         * SolrQueryService.getProductsAndVariantsBySearchTerm(searchTerm, language, market, rows, start)
         * @param searchTerm - Examples: Age, Age*, *Age*, etc.
         * @param language - Example: "en"
         * @param market   - Example: "US"
         * @param rows - Number of results to return
         * @param start - Used for pagination.
         * @param orderTypes - Example: [OrderType.ADR, OrderType.ORDER, OrderType.ZPO] -- default: [OrderType.ADR, OrderType.ORDER]
         *
         * @returns A promise to the result which may contain general products and variant products. Base products are excluded.
         */
        // getProductsAndVariantsBySearchTerm: function(searchTerm, language, market, rows, start){
        //     return getProductsBySearchTerm(ProductResultsEnum.PRODUCTS_AND_VARIANT_PRODUCTS, searchTerm, language, market, rows, start);
        // },
        getProductsAndVariantsBySearchTerm: function(searchTerm, language, market, rows, start, productSearchFields, orderTypes){
            return getProductsBySearchTerm(
                ProductResultsEnum.PRODUCTS_AND_VARIANT_PRODUCTS, searchTerm, language, market, rows, start, productSearchFields, orderTypes
            );
        },


        /**
         * Find all products including base and variant products that have a category field that matches the searchTerm.
         * If the search term doesn't contain any wild characters we will be search for an exact match for the value of searchTerm.
         *
         * @memberof SolrQueryService
         * @example
         * SolrQueryService.getAllProductsByCategory(searchTerm, language, market, rows, start)
         * @param searchTerm - Examples: Age, Age*, *Age*, etc.
         * @param language - Example: "en"
         * @param market   - Example: "US"
         * @param rows - Number of results to return
         * @param start - Used for pagination.
         *
         * @returns A promise to the result which may contain general products, base products, and variant products.
         */
        getAllProductsByCategory: function(searchTerm, language, market, rows, start) {
            return getProductsByCategory(ProductResultsEnum.ALL_PRODUCTS, searchTerm, language, market, rows, start);
        },

        /**
         * Find products and base products that have a category field that matches the searchTerm. If the search term
         * doesn't contain any wild characters we will be search for an exact match for the value of searchTerm.
         *
         * @memberof SolrQueryService
         * @example
         * SolrQueryService.getProductsAndBasesByCategory(searchTerm, language, market, rows, start)
         * @param searchTerm - Examples: Age, Age*, *Age*, etc.
         * @param language - Example: "en"
         * @param market   - Example: "US"
         * @param rows - Number of results to return
         * @param start - Used for pagination.
         *
         * @returns A promise to the result which may contain general products and base products. Variant products are excluded.
         */
        getProductsAndBasesByCategory: function(searchTerm, language, market, rows, start) {
            return getProductsByCategory(ProductResultsEnum.PRODUCTS_AND_BASE_PRODUCTS, searchTerm, language, market, rows, start);
        },

        /**
         * Find products and variant products that have a category field that matches the searchTerm. If the search
         * term doesn't contain any wild characters we will be search for an exact match for the value of searchTerm.
         *
         * @memberof SolrQueryService
         * @example
         * SolrQueryService.getProductsAndVariantsByCategory(searchTerm, language, market, rows, start)
         * @param searchTerm - Examples: Age, Age*, *Age*, etc.
         * @param language - Example: "en"
         * @param market   - Example: "US"
         * @param rows - Number of results to return
         * @param start - Used for pagination.
         *
         * @returns A promise to the result which may contain general products and variant products. Base products are excluded.
         */
        getProductsAndVariantsByCategory: function(searchTerm, language, market, rows, start) {
            return getProductsByCategory(ProductResultsEnum.PRODUCTS_AND_VARIANT_PRODUCTS, searchTerm, language, market, rows, start);
        },

        /**
         * Get all base products for the particular market and language.
         *
         * @memberof SolrQueryService
         * @example
         * SolrQueryService.getAllBaseProducts(language, market, rows, start)
         * @param language
         * @param market
         * @param rows
         * @param start
         * @returns {*}
         */
        getAllBaseProducts: function(language, market, rows, start){
            return getProducts(ProductResultsEnum.BASE_PRODUCTS, language, market, rows, start);
        },

        /**
         * Get all the variant products for the particular market and language.
         *
         * @memberof SolrQueryService
         * @example
         * SolrQueryService.getAllVariantProducts(language, market, rows, start)
         * @param language
         * @param market
         * @param rows
         * @param start
         * @returns {*}
         */
        getAllVariantProducts: function(language, market, rows, start){
            return getProducts(ProductResultsEnum.VARIANT_PRODUCTS, language, market, rows, start);
        },

        /**
         * Find all products that match SKUs passed in the searchTerm.
         *
         * @memberof SolrQueryService
         * @example
         * SolrQueryService.getProductsBySKUArray(skuArray, language, market)
         * @param skuArray - Examples: 01003049, 01003061 etc.
         * @param language - Example: "en"
         * @param market   - Example: "US"
         *
         * @returns A promise to the result which may contain general products and variant products. Base products are excluded.
         */
        getProductsBySKUArray: function(skuArray, language, market) {
            return getProductsBySKUArray(skuArray, language, market);
        },

        /**
         * Find products and base products that have a field that matches the searchTerm. If the search term doesn't
         * contain any wild characters we will be search for an exact match for the value of searchTerm.while fetching
         * the results giving more weight to ProductTitle field.
         * Solr query used eDisMax parser.
         *
         * @memberof SolrQueryService
         * @example
         * SolrQueryService.getProductsAndBasesBySearchTermWithFieldLevelBoosting(searchTerm, language, market, rows, start)
         * @param searchTerm - Examples: Age, Age*, *Age*, etc.
         * @param language - Example: "en"
         * @param market   - Example: "US"
         * @param rows - Number of results to return
         * @param start - Used for pagination.
         *
         * @returns A promise to the result which may contain general products and base products. Variant products are excluded.
         */
        getProductsAndBasesBySearchTermWithFieldLevelBoosting: function(searchTerm, language, market, rows, start) {
            return getProductsBySearchTermWithFieldLevelBoosting(
                ProductResultsEnum.PRODUCTS_AND_BASE_PRODUCTS, searchTerm, language, market, rows, start
            );
        },
        /**
         * Find products and variant products that have a field that matches the searchTerm. If the search term doesn't
         * contain any wild characters we will be search for an exact match for the value of searchTerm.
         *
         * @memberof SolrQueryService
         * @example
         * SolrQueryService.getProductsAndVariantsBySearchTermWithFieldLevelBoosting(searchTerm, language, market, rows, start)
         * @param searchTerm - Examples: Age, Age*, *Age*, etc.
         * @param language - Example: "en"
         * @param market   - Example: "US"
         * @param rows - Number of results to return
         * @param start - Used for pagination.
         *
         * @returns A promise to the result which may contain general products and variant products. Base products are excluded.
         */
        getProductsAndVariantsBySearchTermWithFieldLevelBoosting: function(searchTerm, language, market, rows, start) {
            return getProductsBySearchTermWithFieldLevelBoosting(
                ProductResultsEnum.PRODUCTS_AND_VARIANT_PRODUCTS, searchTerm, language, market, rows, start
            );
        }
    };

    //---------------------------------------------------------------------------
    //
    //                           Private Variables
    //
    //---------------------------------------------------------------------------
    const SolrTimeout = 3000;

    //---------------------------------------------------------------------------
    //
    //                           Private Methods
    //
    //---------------------------------------------------------------------------
    function getProductsBySearchTerm(productResultType, searchTerm, language, market, rows, start, productSearchFields, orderTypes){
        if (typeof(productSearchFields) === "undefined" || (productSearchFields.constructor === Array && productSearchFields.length < 1)) {
            productSearchFields = [];

            // Search all fields except "products". We exclude "prducts" because its values are made up of some of the
            // fields combined.
            productSearchFields.push(ProductSearchFieldsEnum.SKU);
            productSearchFields.push(ProductSearchFieldsEnum.TITLE);
            productSearchFields.push(ProductSearchFieldsEnum.DESCRIPTION);
            productSearchFields.push(ProductSearchFieldsEnum.LONG_DESCRIPTION);
            productSearchFields.push(ProductSearchFieldsEnum.KEY_INGREDIENTS);
            productSearchFields.push(ProductSearchFieldsEnum.CATEGORIES);
            productSearchFields.push(ProductSearchFieldsEnum.SEARCH_TERMS);
            productSearchFields.push(ProductSearchFieldsEnum.USAGE);
            productSearchFields.push(ProductSearchFieldsEnum.BENEFITS);
        }
        if (typeof(orderTypes) === "undefined" || (orderTypes.constructor === Array && orderTypes.length < 1) || orderTypes === null) {
            orderTypes = [OrderType.ADR, OrderType.ORDER];
        }

        return getProductsBySearchFields(productResultType, searchTerm, productSearchFields, language, market, rows, start, orderTypes);
    }

    function getBasicProductsBySearchTerm(productResultType, searchTerm, language, market, rows, start, productSearchFields, orderTypes){
        if (typeof(productSearchFields) === "undefined" || (productSearchFields.constructor === Array && productSearchFields.length < 1)) {
            productSearchFields = [];

            // Search all fields except "products". We exclude "prducts" because its values are made up of some of the
            // fields combined.
            productSearchFields.push(ProductSearchFieldsEnum.SKU);
            productSearchFields.push(ProductSearchFieldsEnum.TITLE);
            productSearchFields.push(ProductSearchFieldsEnum.DESCRIPTION);
            productSearchFields.push(ProductSearchFieldsEnum.LONG_DESCRIPTION);
            productSearchFields.push(ProductSearchFieldsEnum.KEY_INGREDIENTS);
            productSearchFields.push(ProductSearchFieldsEnum.CATEGORIES);
            productSearchFields.push(ProductSearchFieldsEnum.SEARCH_TERMS);
            productSearchFields.push(ProductSearchFieldsEnum.USAGE);
            productSearchFields.push(ProductSearchFieldsEnum.BENEFITS);
        }

        return getBasicProductsBySearchFields(productResultType, searchTerm, productSearchFields, language, market, rows, start, orderTypes);
    }

    function getProductsByCategory(productResultType, searchTerm, language, market, rows, start, orderTypes){
        let productSearchFields = [];
        productSearchFields.push(ProductSearchFieldsEnum.CATEGORIES);
        if (typeof(orderTypes) === "undefined" || (orderTypes.constructor === Array && orderTypes.length < 1) || orderTypes === null) {
            orderTypes = [OrderType.ADR, OrderType.ORDER];
        }

        return getProductsBySearchFields(productResultType, searchTerm, productSearchFields, language, market, rows, start, orderTypes);
    }

    function getProductsBySKUArray(skuArray, language, market, deferred){
        //deferred is an optional parameter.  If this function is called by itself, it will pass in the deferred object
        // created when called without the deferred parameter
        let retrySolrRequest = true;
        if (typeof(deferred) !== 'object') {
            deferred = $.Deferred();
        }
        else {
            retrySolrRequest = false;
        }
        let _skuArray = skuArray;
        let productSearchFields = [];
        productSearchFields.push(ProductSearchFieldsEnum.SKU);

        let matchingRootProducts = SolrQueryUrlService.getProductRootsForSKUArray(skuArray, productSearchFields, language, market);

        // eslint-disable-next-line
        // Example solrUrl: http://dev-gateway-mulesoft-21:8080/mec/solr/oak/select?fl=*,[child%20parentFilter=path_exact:product/????????%20childFilter=path_exact:product/????????/en],[child%20parentFilter=path_exact:product/????????%20childFilter=path_exact:product/????????/US]&q=path_exact:product/01003611&rows=5&wt=json
        let solrUrl = SolrQueryUrlService.getProductsByRoots(matchingRootProducts, language, market, (matchingRootProducts.length)+10, 0);

        if (solrUrl.url) {
            let productDocuments = [];

            // Return the complete records for the given matches
            $.ajax ({
                url: solrUrl.url,
                type: "POST",
                contentType: 'application/json; charset=utf-8',
                headers: {
                    "client_id" : ConfigService.getMarketConfig().solrClientId,
                    "client_secret" : ConfigService.getMarketConfig().solrClientSecret
                },
                timeout: SolrTimeout,
                data: JSON.stringify(solrUrl.postData),
                success: function (responseData) {
                    let docs, responseHeader;

                    if (typeof(responseData) === "string") {
                        docs = $.parseJSON(responseData).response.docs;
                        responseHeader = $.parseJSON(responseData).responseHeader;
                    }
                    else {
                        docs = responseData.response.docs;
                        responseHeader = responseData.responseHeader;
                    }

                    if (responseHeader.zkConnected || !retrySolrRequest) {
                        // Wrap the products in an adaptor for ease of use.
                        for (let i = 0; i < docs.length; i++) {
                            //ONLY RETURN PRODUCTS THAT HAVE A MARKET
                            if (docs[i]._childDocuments_) {
                                for (let m = 0; m < docs[i]._childDocuments_.length; m++) {
                                    if ((docs[i]._childDocuments_[m].path_exact === "product/" + docs[i].sku + '/' + market.toUpperCase()) &&
                                        !docs[i]._childDocuments_[m].excludeFromSearch) {

                                        if (!skuExistsInSolrProductAdaptorArray(productDocuments, docs[i].sku.toString())) {
                                            productDocuments.push(new SolrProductAdaptor(docs[i]));
                                        }
                                    }
                                }
                            }
                        }

                        productDocuments = orderProductsBySkuArray(productDocuments, _skuArray);
                        deferred.resolve(productDocuments);
                    } else {
                        getProductsBySKUArray(skuArray, language, market, deferred);
                    }
                },
                error: function (xmlHttpRequest, textStatus) {
                    deferred.reject(textStatus);
                    //console.log('Search failed!');
                }
            });
        }
        else
        {
            deferred.resolve(undefined);
        }

        return deferred;
    }

    function getBasicProductsBySearchFields(productResultType, searchTerm, productSearchFields, language, market, rows, start, orderTypes, deferred){
        let searchByOrderType = false;
        if (typeof(orderTypes) !== "undefined" && orderTypes !== null) {
            searchByOrderType = true;
        }
        // deferred is an optional parameter.  If this function is called by itself, it will pass in the deferred object
        // created when called without the deferred parameter
        if (typeof(deferred) !== 'object') {
            deferred = $.Deferred();
        }

        // eslint-disable-next-line
        // Example solrUrl: http://dev-gateway-mulesoft-21:8080/mec/solr/oak/select?fl=_root_&fq=-baseSku:[* TO *] && markets:US&fq=categories:men&q=path_exact:product/????????/en&rows=5&wt=json
        // eslint-disable-next-line
        //var solrUrl = SolrQueryUrlService.getProductRootsForSearchTerms(productResultType, searchTerm, productSearchFields, language, market, rows, start);
        let solrUrl = SolrQueryUrlService.getBasicProductDataForSearchTermsWithFieldLevelBoosting(
            productResultType, searchTerm, productSearchFields, language, market, searchByOrderType ? rows*5 : rows, start
        );

        // Search for language nodes for the given market that match the search term.
        let queryResponsePromise = $.ajax({
            url: solrUrl,
            type: "GET",
            contentType: 'application/json; charset=utf-8',
            headers: {
                "client_id": ConfigService.getMarketConfig().solrClientId,
                "client_secret": ConfigService.getMarketConfig().solrClientSecret
            },
            timeout: SolrTimeout,
            success: function () {
                //console.log("Search success!");
            },
            error: function (xmlHttpRequest, textStatus) {
                deferred.reject(textStatus);
                //console.log('Search failed!');
            }
        });

        queryResponsePromise.then(function(responseData) {
            let retValue;
            let docs, totalNumFound/*, responseHeader*/;

            if (typeof(responseData) === "string") {
                docs = $.parseJSON(responseData).response.docs;
                totalNumFound = $.parseJSON(responseData).response.numFound;
                // responseHeader = $.parseJSON(responseData).responseHeader;
            } else {
                docs = responseData.response.docs;
                totalNumFound = responseData.response.numFound;
                // responseHeader = responseData.responseHeader;
            }

            if (searchByOrderType) {
                solrUrl = SolrQueryUrlService.getProductsByRoots(docs, language, market, rows, 0, orderTypes);
                let _orderTypes = orderTypes;
                if (solrUrl.url) {
                    let productDocuments = [];
                    queryResponsePromise = $.ajax({
                        url: solrUrl.url,
                        type: "POST",
                        contentType: 'application/json; charset=utf-8',
                        headers: {
                            "client_id": ConfigService.getMarketConfig().solrClientId,
                            "client_secret": ConfigService.getMarketConfig().solrClientSecret
                        },
                        timeout: SolrTimeout,
                        data: JSON.stringify(solrUrl.postData),
                        success: function (responseData) {
                            if (typeof(responseData) === "string") {
                                docs = $.parseJSON(responseData).response.docs;
                                totalNumFound = $.parseJSON(responseData).response.numFound;
                            }
                            else {
                                docs = responseData.response.docs;
                                totalNumFound = responseData.response.numFound;
                            }

                            // Wrap the products in an adaptor for ease of use.
                            for (let i = 0; i < docs.length; i++) {
                                //ONLY RETURN PRODUCTS THAT HAVE A MARKET
                                if (docs[i]._childDocuments_) {
                                    for (let m = 0; m < docs[i]._childDocuments_.length; m++) {
                                        if ((docs[i]._childDocuments_[m].path_exact === "product/" + docs[i].sku + '/' + market.toUpperCase()) &&
                                            !docs[i]._childDocuments_[m].excludeFromSearch) {

                                            if (!skuExistsInProductArray(productDocuments, docs[i].sku.toString())) {
                                                let prodAdaptor = new SolrProductAdaptor(docs[i]);
                                                let marketChild = prodAdaptor.getMarketChild(market);
                                                let languageChild = prodAdaptor.getLanguageChild(language);
                                                if (existsInArray(marketChild.getOrderType(), _orderTypes)) {
                                                    productDocuments.push({
                                                        sku: docs[i].sku,
                                                        fullImage: marketChild.getFullImageUrl(),
                                                        name: languageChild.getTitle(),
                                                        _root_: "product/" + docs[i].sku,
                                                        isExclusive: languageChild.getIsExclusive()
                                                    });
                                                }
                                            }
                                        }
                                    }
                                }
                            }

                            let retValue = {
                                products: productDocuments,
                                totalNumFound: totalNumFound
                            };

                            deferred.resolve(retValue);
                        },
                        error: function (xmlHttpRequest, textStatus) {
                            deferred.reject(textStatus);
                            //console.log('Search failed!');
                        }
                    });
                } else {
                    retValue = {
                        products: docs,
                        totalNumFound: totalNumFound
                    };
                    deferred.resolve(retValue);
                }
            } else {
                retValue = {
                    products: docs,
                    totalNumFound: totalNumFound
                };

                deferred.resolve(retValue);
            }
        });

        return deferred;
    }

    function getProductsBySearchFields(productResultType, searchTerm, productSearchFields, language, market, rows, start, orderTypes, deferred){
        //deferred is an optional parameter.  If this function is called by itself, it will pass in the deferred object created
        // when called without the deferred parameter
        let retrySolrRequest;
        if (typeof(deferred) !== 'object') {
            deferred = $.Deferred();
            retrySolrRequest = true;
        }
        else {
            retrySolrRequest = false;
        }

        // eslint-disable-next-line
        // Example solrUrl: http://dev-gateway-mulesoft-21:8080/mec/solr/oak/select?fl=_root_&fq=-baseSku:[* TO *] && markets:US&fq=categories:men&q=path_exact:product/????????/en&rows=5&wt=json
        // eslint-disable-next-line
        //var solrUrl = SolrQueryUrlService.getProductRootsForSearchTerms(productResultType, searchTerm, productSearchFields, language, market, rows, start);
        let solrUrl = SolrQueryUrlService.getProductRootsForSearchTermsWithFieldLevelBoosting(
            productResultType, searchTerm, productSearchFields, language, market, rows, start
        );

        // Search for language nodes for the given market that match the search term.
        let queryResponsePromise = $.ajax({
            url: solrUrl,
            type: "GET",
            contentType: 'application/json; charset=utf-8',
            headers: {
                "client_id": ConfigService.getMarketConfig().solrClientId,
                "client_secret": ConfigService.getMarketConfig().solrClientSecret
            },
            timeout: SolrTimeout,
            success: function () {
                //console.log("Search success!");
            },
            error: function (xmlHttpRequest, textStatus) {
                deferred.reject(textStatus);
                //console.log('Search failed!');
            }
        });

        queryResponsePromise.then(function(responseData){
            let matchingRootProducts, totalNumFound, responseHeader;

            if (typeof(responseData) === "string") {
                matchingRootProducts = $.parseJSON(responseData).response.docs;
                totalNumFound = $.parseJSON(responseData).response.numFound;
                responseHeader = $.parseJSON(responseData).responseHeader;
            } else {
                matchingRootProducts = responseData.response.docs;
                totalNumFound = responseData.response.numFound;
                responseHeader = responseData.responseHeader;
            }

            // If the zkConnected header is true, we count on the data returned from the query being accurate.
            // If it's false, we will re-try the Solr query so that we are sure we have the most up-to-date information
            // (We only try again once, which is what sets the retrySolrRequest variable)
            if (responseHeader.zkConnected || !retrySolrRequest) {
                // eslint-disable-next-line
                // Example solrUrl: http://dev-gateway-mulesoft-21:8080/mec/solr/oak/select?fl=*,[child%20parentFilter=path_exact:product/????????%20childFilter=path_exact:product/????????/en],[child%20parentFilter=path_exact:product/????????%20childFilter=path_exact:product/????????/US]&q=path_exact:product/01003611&rows=5&wt=json
                // Always start at 0 so we get all the rows we are asking for.
                solrUrl = SolrQueryUrlService.getProductsByRoots(matchingRootProducts, language, market, rows, 0, orderTypes);

                if (solrUrl.url) {
                    let productDocuments = [];

                    // Return the complete records for the given matches
                    queryResponsePromise = $.ajax({
                        url: solrUrl.url,
                        type: "POST",
                        contentType: 'application/json; charset=utf-8',
                        headers: {
                            "client_id": ConfigService.getMarketConfig().solrClientId,
                            "client_secret": ConfigService.getMarketConfig().solrClientSecret
                        },
                        data: JSON.stringify(solrUrl.postData),
                        timeout: SolrTimeout,
                        success: function (responseData) {
                            let docs;

                            if (typeof(responseData) === "string") {
                                docs = $.parseJSON(responseData).response.docs;
                            }
                            else {
                                docs = responseData.response.docs;
                            }

                            // Wrap the products in an adaptor for ease of use.
                            for (let i = 0; i < docs.length; i++) {
                                //ONLY RETURN PRODUCTS THAT HAVE A MARKET
                                if (docs[i]._childDocuments_) {
                                    for (let m = 0; m < docs[i]._childDocuments_.length; m++) {
                                        if ((docs[i]._childDocuments_[m].path_exact === "product/" + docs[i].sku + '/' + market.toUpperCase()) &&
                                            !docs[i]._childDocuments_[m].excludeFromSearch) {

                                            if (!skuExistsInSolrProductAdaptorArray(productDocuments, docs[i].sku.toString())) {
                                                let score = 0;
                                                for (let s = i; s < matchingRootProducts.length; s++) {
                                                    if (matchingRootProducts[s]._root_[0] == docs[i].path_exact) {
                                                        score = matchingRootProducts[s].score;
                                                        break;
                                                    }
                                                }
                                                productDocuments.push(new SolrProductAdaptor(docs[i], score));
                                            }
                                        }
                                    }
                                }
                            }

                            let retValue = {
                                products: productDocuments,
                                totalNumFound: totalNumFound
                            };

                            deferred.resolve(retValue);
                        },
                        error: function (xmlHttpRequest, textStatus) {
                            deferred.reject(textStatus);
                            //console.log('Search failed!');
                        }
                    });
                }
                else {
                    deferred.resolve(undefined);
                }
            } else {
                getProductsBySearchFields(productResultType, searchTerm, productSearchFields, language, market, rows, start, orderTypes, deferred)
            }
        });

        return deferred;
    }

    function getProducts(productResultType, language, market, rows, start){
        let deferred = $.Deferred();
        let solrUrl = SolrQueryUrlService.getProducts(productResultType, language, market, rows, start);
        let productDocuments = [];

        // Search for language nodes for the given market that match the search term.
        $.ajax({
            url: solrUrl.url,
            type: "POST",
            contentType: 'application/json; charset=utf-8',
            headers: {
                "client_id": ConfigService.getMarketConfig().solrClientId,
                "client_secret": ConfigService.getMarketConfig().solrClientSecret
            },
            data: JSON.stringify(solrUrl.postData),
            timeout: SolrTimeout,
            success: function (responseData) {
                let docs;
                let totalNumFound;
                if (typeof(responseData) === "string") {
                    docs = $.parseJSON(responseData).response.docs;
                    totalNumFound = $.parseJSON(responseData).response.numFound;
                } else {
                    docs = responseData.response.docs;
                    totalNumFound = responseData.response.numFound;
                }

                // Wrap the products in an adaptor for ease of use.
                for (let i = 0; i < docs.length; i++) {
                    //ONLY RETURN PRODUCTS THAT HAVE A MARKET
                    if (docs[i]._childDocuments_) {
                        for (let m = 0; m < docs[i]._childDocuments_.length; m++) {
                            if ((docs[i]._childDocuments_[m].path_exact === "product/" + docs[i].sku + '/' + market.toUpperCase()) &&
                                !docs[i]._childDocuments_[m].excludeFromSearch) {

                                if (!skuExistsInSolrProductAdaptorArray(productDocuments, docs[i].sku.toString())) {
                                    productDocuments.push(new SolrProductAdaptor(docs[i]));
                                }
                            }
                        }
                    }
                }

                let retValue = {
                    products: productDocuments,
                    totalNumFound: totalNumFound
                };

                deferred.resolve(retValue);
            },
            error: function (xmlHttpRequest, textStatus) {
                deferred.reject(textStatus);
                //console.log('Search failed!');
            }
        });

        return deferred;
    }

    function getProductsBySearchTermWithFieldLevelBoosting(productResultType, searchTerm, language, market, rows, start){
        let productSearchFields = [];

        // Search all fields except "products". We exclude "prducts" because its values are made up of some of the
        // fields combined.
        productSearchFields.push(ProductSearchFieldsEnum.SKU);
        productSearchFields.push(ProductSearchFieldsEnum.TITLE);
        productSearchFields.push(ProductSearchFieldsEnum.DESCRIPTION);
        productSearchFields.push(ProductSearchFieldsEnum.LONG_DESCRIPTION);
        productSearchFields.push(ProductSearchFieldsEnum.KEY_INGREDIENTS);
        productSearchFields.push(ProductSearchFieldsEnum.CATEGORIES);
        productSearchFields.push(ProductSearchFieldsEnum.SEARCH_TERMS);
        productSearchFields.push(ProductSearchFieldsEnum.USAGE);
        productSearchFields.push(ProductSearchFieldsEnum.BENEFITS);

        return getProductsBySearchFieldsWithFieldLevelBoosting(productResultType, searchTerm, productSearchFields, language, market, rows, start);
    }

    function getProductsBySearchFieldsWithFieldLevelBoosting(productResultType, searchTerm, productSearchFields, language, market, rows, start){
        let deferred = $.Deferred();

        // eslint-disable-next-line
        //Example : http://test-solr.nuskin.net:8983/solr/oak/select?defType=edismax&fl=_root_,score,productTitle&indent=on&q=ageLOC%20Body%20Shaping%20Gel*&qf=productTitle^1.5+keyIng+sku+description&sort=score%20desc,productSuggestion%20asc&wt=json&fq=markets:US&fq=path_exact:product/????????/en&rows=30&fq=-variants:*
        let solrUrl = SolrQueryUrlService.getProductRootsForSearchTermsWithFieldLevelBoosting(
            productResultType, searchTerm, productSearchFields, language, market, rows, start
        );

        // Search for language nodes for the given market that match the search term.
        let queryResponsePromise = $.ajax({
            url: solrUrl,
            type: "GET",
            contentType: 'application/json; charset=utf-8',
            headers: {
                "client_id": ConfigService.getMarketConfig().solrClientId,
                "client_secret": ConfigService.getMarketConfig().solrClientSecret
            },
            timeout: SolrTimeout,
            success: function () {
                //console.log("Search success!");
            },
            error: function (xmlHttpRequest, textStatus) {
                deferred.reject(textStatus);
                //console.log('Search failed!');
            }
        });

        queryResponsePromise.then(function(responseData) {
            let matchingRootProducts;
            let totalNumFound;
            if (typeof(responseData) === "string") {
                matchingRootProducts = $.parseJSON(responseData).response.docs;
                totalNumFound = $.parseJSON(responseData).response.numFound;
            } else {
                matchingRootProducts = responseData.response.docs;
                totalNumFound = responseData.response.numFound;
            }

            // eslint-disable-next-line
            // Example solrUrl: http://dev-gateway-mulesoft-21:8080/mec/solr/oak/select?fl=*,[child%20parentFilter=path_exact:product/????????%20childFilter=path_exact:product/????????/en],[child%20parentFilter=path_exact:product/????????%20childFilter=path_exact:product/????????/US]&q=path_exact:product/01003611&rows=5&wt=json
            // Always start at 0 so we get all the rows we are asking for.
            solrUrl = SolrQueryUrlService.getProductsByRootsWithTermBoosting(matchingRootProducts, language, market, rows, 0);

            if (solrUrl.length > 0) {
                let productDocuments = [];

                // Return the complete records for the given matches
                queryResponsePromise = $.ajax ({
                    url: solrUrl,
                    type: "GET",
                    contentType: 'application/json; charset=utf-8',
                    headers: {
                        "client_id" : ConfigService.getMarketConfig().solrClientId,
                        "client_secret" : ConfigService.getMarketConfig().solrClientSecret
                    },
                    timeout: SolrTimeout,
                    success: function(responseData) {
                        let docs;
                        if (typeof(responseData) === "string") {
                            docs = $.parseJSON(responseData).response.docs;
                        } else {
                            docs = responseData.response.docs;
                        }

                        // Wrap the products in an adaptor for ease of use.
                        for (let i = 0; i < docs.length; i++) {
                            //ONLY RETURN PRODUCTS THAT HAVE A MARKET AND AREN'T EXCLUDED FROM SEARCH
                            if (docs[i]._childDocuments_) {
                                for (let m = 0; m < docs[i]._childDocuments_.length; m++) {
                                    if ((docs[i]._childDocuments_[m].path_exact === "product/" + docs[i].sku + '/' + market.toUpperCase()) &&
                                        !docs[i]._childDocuments_[m].excludeFromSearch) {

                                        if (!skuExistsInSolrProductAdaptorArray(productDocuments, docs[i].sku.toString())) {
                                            productDocuments.push(new SolrProductAdaptor(docs[i]));
                                        }
                                    }
                                }
                            }
                        }

                        let retValue = {
                            products: productDocuments,
                            totalNumFound: totalNumFound
                        };

                        deferred.resolve(retValue);
                    },
                    error: function(xmlHttpRequest, textStatus) {
                        deferred.reject(textStatus);
                        //console.log('Search failed!');
                    }
                });
            }
            else
            {
                deferred.resolve(undefined);
            }
        });

        return deferred;
    }

    function orderProductsBySkuArray(products, skuArray) {
        let prods = [];
        for (let i = 0; i < skuArray.length; i++)
        {
            for (let j = 0; j < products.length; j++) {
                if (skuArray[i] == products[j].getSku())
                    prods.push(products[j]);
            }
        }
        return prods;
    }

    function skuExistsInSolrProductAdaptorArray(products, sku) {
        let exists = Object.keys(products).some(function(k) {
            return products[k].getSku() === sku;
        });
        return exists;
    }

    function skuExistsInProductArray(products, sku) {
        let exists = Object.keys(products).some(function(k) {
            return products[k].sku === sku;
        });
        return exists;
    }

    function existsInArray(searchArray, valuesArray) {
        if (searchArray && valuesArray) {
            return valuesArray.some(function (v) {
                return searchArray.indexOf(v) >= 0;
            });
        }
        return false;
    }

    //---------------------------------------------------------------------------
    //
    //                           Tests
    //
    //---------------------------------------------------------------------------
    // var rows = 10;
    // var start = 0;
    //
    // publicMethods.getAllBaseProducts("en", "US").then(function(results){
    //     console.log("It works!");
    // });
    //
    // publicMethods.getAllVariantProducts("en", "US").then(function(results){
    //     console.log("It works!");
    // });
    //
    // publicMethods.getProductsAndBasesBySearchTerm("a", "en", "US", rows, start).then(function(results){
    //     var baseSku;
    //     var childBaseSku;
    //
    //     if (results.products) {
    //         for (var i = 0; i < results.products.length; i++) {
    //             baseSku = results.products[i].getBaseSku();
    //             childBaseSku = results.products[i].getLanguageChild("en").getBaseSku();
    //         }
    //     }
    // });

    // publicMethods.getAllProductsBySearchTerm("ageLoc Youth", "en", "US").then(function(results){
    //     var baseSku;
    //     var childBaseSku;
    //
    //     if (results.products) {
    //         for (var i = 0; i < results.products.length; i++) {
    //             baseSku = results.products[i].getBaseSku();
    //             childBaseSku = results.products[i].getLanguageChild("en").getBaseSku();
    //         }
    //     }
    // });


    // For variant product
    // Should produce 1 result
    // publicMethods.getAllProductsBySearchTerm("01010260", "en", "US").then(function(results){
    //     var baseSku;
    //     var childBaseSku;
    //
    //     if (results.products) {
    //         for (var i = 0; i < results.products.length; i++) {
    //             baseSku = results.products[i].getBaseSku();
    //             childBaseSku = results.products[i].getLanguageChild("en").getBaseSku();
    //         }
    //     }
    // });

    // Should produce 1 result
    // publicMethods.getProductsAndVariantsBySearchTerm("01010260", "en", "US").then(function(results){
    //     var baseSku;
    //     var childBaseSku;
    //
    //     if (results.products) {
    //         for (var i = 0; i < results.products.length; i++) {
    //             baseSku = results.products[i].getBaseSku();
    //             childBaseSku = results.products[i].getLanguageChild("en").getBaseSku();
    //         }
    //     }
    // });

    // Should produce 0 results
    // publicMethods.getProductsAndBasesBySearchTerm("01010260", "en", "US").then(function(results){
    //     var variants;
    //     var childVariants;
    //
    //     if (results.products) {
    //         for (var i = 0; i < results.products.length; i++) {
    //             variants = results.products[i].getVariants();
    //             childVariants = results.products[i].getLanguageChild("en").getVariants();
    //         }
    //     }
    // });

    // For base product
    // Should produce 1 result
    // publicMethods.getAllProductsBySearchTerm("01550002", "en", "US").then(function(results){
    //     var variants;
    //     var childVariants;
    //
    //     if (results.products) {
    //         for (var i = 0; i < results.products.length; i++) {
    //             variants = results.products[i].getVariants();
    //             childVariants = results.products[i].getLanguageChild("en").getVariants();
    //         }
    //     }
    // });

    // Should produce 1 result
    // publicMethods.getProductsAndBasesBySearchTerm("01550002", "en", "US").then(function(results){
    //     var variants;
    //     var childVariants;
    //
    //     if (results.products) {
    //         for (var i = 0; i < results.products.length; i++) {
    //             variants = results.products[i].getVariants();
    //             childVariants = results.products[i].getLanguageChild("en").getVariants();
    //         }
    //     }
    // });

    // Should produce 0 results
    // publicMethods.getProductsAndVariantsBySearchTerm("01550002", "en", "US").then(function(results){
    //     var baseSku;
    //     var childBaseSku;
    //
    //     if (results.products) {
    //         for (var i = 0; i < results.products.length; i++) {
    //             baseSku = results.products[i].getBaseSku();
    //             childBaseSku = results.products[i].getLanguageChild("en").getBaseSku();
    //         }
    //     }
    // });

    return publicMethods;
}();

export default SolrQueryService;
